import { Link } from "react-router-dom";
import SectionHeader from "@/components/SectionHeader";
import { IMG } from "@/lib/api";
import { Check, ArrowUpRight } from "lucide-react";

const PROGRAMS = [
  {
    name: "Foundations",
    tagline: "For new licensees",
    points: ["Lead-gen rituals that compound", "First 90 days, fully scripted", "Database setup & nurture flows", "Mindset & money management"],
    duration: "8 weeks",
  },
  {
    name: "Momentum",
    tagline: "For agents in their 2nd–4th year",
    points: ["Niche & brand sharpening", "Listing-side mastery", "Negotiation & contract craft", "Referral engine activation"],
    duration: "12 weeks",
  },
  {
    name: "Lifestyle Leverage",
    tagline: "For top producers",
    points: ["Team build & systems", "Luxury market positioning", "Wealth strategy & investing", "Brand as a business asset"],
    duration: "6 months",
  },
];

export default function Coaching() {
  return (
    <div data-testid="coaching-page">
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img src={IMG.lifestyle2} alt="Coaching" className="absolute inset-0 w-full h-full object-cover img-warm" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60" />
        <div className="relative h-full container-edge flex items-end pb-16">
          <div className="text-white max-w-2xl">
            <p className="overline text-[#e8c79a]">Coaching</p>
            <h1 className="hero-headline mt-4 text-5xl md:text-6xl lg:text-7xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>
              Coaching with calm clarity.
            </h1>
            <p className="mt-6 text-lg text-white/90 max-w-xl font-light">
              Build a real estate practice that actually fits your life. No hustle culture —
              just systems, sharpness, and the quiet confidence to lead.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader
              overline="The philosophy"
              title="Less noise. More signal."
              subtitle="Coaching shouldn't feel like another podcast on 2× speed. We work the fundamentals — slowly, intentionally, until they become the way you show up every day."
            />
          </div>
          <div className="lg:col-span-7 lg:pl-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {[
              { t: "1:1 Mentorship", d: "Weekly calls, async messaging, and real accountability." },
              { t: "Field Coaching", d: "Listing visits, open houses, ride-alongs when local." },
              { t: "Systems & Tech", d: "CRM setup, templates, and the stack we actually use." },
              { t: "Mindset", d: "Boundaries, money, and a calm relationship with the work." },
            ].map((b, i) => (
              <div key={b.t} className="border-l-2 border-soft pl-6" data-testid={`coaching-benefit-${i}`}>
                <h4 style={{ fontFamily: "Playfair Display, serif" }} className="text-xl">{b.t}</h4>
                <p className="mt-3 text-soft text-sm leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-linen" data-testid="coaching-programs">
        <SectionHeader overline="Programs" title="Three doors. Pick the one that fits." />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {PROGRAMS.map((p, idx) => (
            <div key={p.name} className="editorial-card p-10 flex flex-col" data-testid={`program-${idx}`}>
              <p className="overline">{p.tagline}</p>
              <h3 style={{ fontFamily: "Playfair Display, serif" }} className="mt-4 text-3xl">{p.name}</h3>
              <p className="text-sand text-sm mt-2">{p.duration}</p>
              <ul className="mt-8 space-y-3 flex-1">
                {p.points.map((pt) => (
                  <li key={pt} className="flex gap-3 text-sm text-soft">
                    <Check size={16} className="text-sand mt-0.5 shrink-0" />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn-outline mt-10 self-start" data-testid={`program-${idx}-cta`}>
                Apply <ArrowUpRight size={14} className="ml-2" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="text-4xl md:text-5xl" style={{ fontFamily: "Playfair Display, serif" }}>
              Curious if we'd be a fit?
            </h2>
            <p className="mt-4 text-soft">Book a no-pressure 20-minute conversation. We'll see.</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" className="btn-primary" data-testid="coaching-final-cta">Book a call</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
